import React from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../PublicNavbar";
import LegalFooter from "./LegalFooter";

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <PublicNavbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">Cancellation & Refund Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: February 17, 2026</p>
          
          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            
            <section className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-amber-400 mb-2">Important Notice</h2>
              <p className="text-gray-300 leading-relaxed">
                Please read this policy carefully before subscribing. By purchasing a subscription to Paladin Health & Human Performance, you acknowledge and agree to the terms outlined below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Automatic Renewal</h2>
              <p className="text-gray-300 leading-relaxed">
                All subscriptions to Paladin Health & Human Performance automatically renew at the end of each billing period (weekly, monthly, or as otherwise specified) until you cancel. Your payment method will be charged automatically on each renewal date at the then-current subscription rate. You authorize these recurring charges by subscribing to the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. How to Cancel</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You may cancel your subscription at any time through one of the following methods:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li><strong className="text-white">Account Dashboard:</strong> Log in to your account and navigate to Settings → Subscription → Cancel Subscription</li>
                <li><strong className="text-white">Email:</strong> Send a cancellation request to support@paladinhhp.com from the email address associated with your account</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Please allow up to 24 hours for cancellation requests made via email to be processed. You will receive a confirmation email once your cancellation has been processed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Effect of Cancellation</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you cancel your subscription:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Cancellation will take effect at the end of your current billing period</li>
                <li>You will retain full access to all paid features until your current billing period ends</li>
                <li>No further charges will be made to your payment method after the current period</li>
                <li>Your account will revert to free-tier access (if available) after your subscription ends</li>
                <li>Your workout history and data will be retained unless you request account deletion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Refund Policy</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong className="text-white">No refunds will be provided for:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Partial billing periods or unused time remaining on your subscription</li>
                <li>Subscription fees already charged for the current billing period</li>
                <li>Failure to cancel before your renewal date</li>
                <li>Dissatisfaction with the Service after the first 7 days of your initial subscription</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Exceptions</h3>
              <p className="text-gray-300 leading-relaxed">
                Refunds may be considered on a case-by-case basis for:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Technical issues preventing access to the Service for an extended period (verified by our support team)</li>
                <li>Duplicate or erroneous charges</li>
                <li>Refunds required by applicable law</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                To request a refund exception, please contact support@paladinhhp.com with details of your situation within 7 days of the charge in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Free Trials</h2>
              <p className="text-gray-300 leading-relaxed">
                If you signed up for a free trial, your payment method will be charged automatically at the end of the trial period unless you cancel before the trial ends. Free trials are only available to new subscribers and are limited to one per person. If you cancel during the trial period, you will lose access to premium features immediately upon cancellation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Price Changes</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to change subscription prices at any time. If we change the price of your subscription, we will notify you at least 30 days in advance via email. The new price will apply to your next billing cycle after the notice period. If you do not agree with the price change, you may cancel your subscription before the new price takes effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Chargebacks</h2>
              <p className="text-gray-300 leading-relaxed">
                If you dispute a charge with your bank or credit card company (a "chargeback") without first attempting to resolve the issue with us, we reserve the right to immediately suspend or terminate your account and pursue any available legal remedies. Chargebacks result in additional fees to us, and we take them seriously. We encourage you to contact our support team at support@paladinhhp.com to resolve any billing issues before initiating a chargeback.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Account Termination by Us</h2>
              <p className="text-gray-300 leading-relaxed">
                If we terminate your account for violation of our <Link to="/legal/terms" className="text-blue-400 hover:underline">Terms of Service</Link>, you will not be entitled to any refund of subscription fees. We reserve the right to terminate accounts that engage in prohibited activities, abuse, or fraud.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Billing Questions</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions about your billing, need to update your payment method, or believe you have been charged in error, please contact us:
              </p>
              <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-white/10">
                <p className="text-white font-semibold">Billing Support</p>
                <p className="text-gray-400">Email: support@paladinhhp.com</p>
                <p className="text-gray-400">Please include your account email and a description of the issue.</p>
              </div>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Summary</h2>
              <ul className="text-gray-300 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Subscriptions renew automatically until you cancel</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Cancel anytime via your account dashboard or by emailing support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Keep access until the end of your current billing period</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>No refunds for unused time (except as required by law)</span>
                </li>
              </ul>
            </section>

          </div>
        </div>
      </div>
      
      <LegalFooter />
    </div>
  );
};

export default CancellationPolicy;
